import { useSearchParams } from 'react-router-dom';
import { useClaimTransferByToken } from './useClaimTransferByToken';

/**
 * The one message a FAILED claim shows. A claim can only fail server-side now
 * (see below), and every server-side rejection returns the same generic string
 * by design — `ClaimCertificateByTokenUseCase`'s `GENERIC_CLAIM_ERROR`, which
 * deliberately withholds the reason so a guessed token leaks nothing.
 */
const CLAIM_FAILURE_MESSAGE =
  'This claim link is invalid or has expired. Please contact the sender for a new one.';

/**
 * VERA-304 — the single claim path for BOTH incoming states: a gift
 * (`GiftInActionsSection`) and a brand transfer (`TransferInActionsSection`).
 *
 * It exists because those two had already diverged once, silently. The VERA-315
 * `-X theirs` fold reverted this call site in both files to
 * `claimToken ? handleClaim : setError(NO_TOKEN_MESSAGE)`; the gift half was
 * restored and the transfer half was not, so an authenticated recipient viewing
 * their own incoming BRAND transfer pressed "Claim ownership" and got a red line
 * telling them to go find an email they may never have opened. That is a dead
 * end this epic had already superseded — reproduced live, not hypothetical.
 * With one implementation there is no half to miss on the next fold.
 *
 * Why no token is needed: the emailed `claim_token` identifies WHICH transfer,
 * it was never the authorization. `POST /transfer/customer/claim` is
 * `@RequireOwner()` and the use-case independently requires the caller to BE the
 * transfer's `to_owner_id` (step 4 there). A signed-in recipient therefore
 * carries strictly MORE proof than someone holding a forwardable link. The token
 * remains the credential for the email route, where there is no session yet.
 */
export const useClaimOwnership = (certificateId: string | undefined) => {
  const [searchParams] = useSearchParams();
  const claimToken = searchParams.get('claim_token');

  const { mutateAsync: claimTransferByToken, isPending } = useClaimTransferByToken();

  /**
   * Resolves rather than throws, so a call site renders the failure in whatever
   * surface it already uses without each one re-deriving the message from the
   * axios error shape.
   */
  const claim = async (): Promise<{ ok: true } | { ok: false; message: string }> => {
    try {
      // Arriving from the email carries a token, so send it. Claiming in-app has
      // none, so identify the transfer by its certificate. Both land on the same
      // server-side recipient check.
      await claimTransferByToken(
        claimToken ? { token: claimToken } : { certificateId: certificateId ?? '' },
      );
      return { ok: true };
    } catch (err: any) {
      return {
        ok: false,
        message: err?.response?.data?.message || CLAIM_FAILURE_MESSAGE,
      };
    }
  };

  return { claim, isPending };
};
